import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icons } from '@ohif/ui-next';
import { Button } from '../Button';

export enum showDialogOption {
  NeverShowDialog = 'never',
  AlwaysShowDialog = 'always',
  ShowOnceAndConfigure = 'configure',
}

interface DialogConfiguration {
  option: showDialogOption;
  days?: number;
}

interface InvestigationalUseDialogProps {
  dialogConfiguration?: DialogConfiguration;
}

const InvestigationalUseDialog = ({
  dialogConfiguration = {
    option: showDialogOption.AlwaysShowDialog,
  },
}: InvestigationalUseDialogProps) => {
  const { t } = useTranslation('InvestigationalUseDialog');
  const { option, days = 30 } = dialogConfiguration;
  const [isHidden, setIsHidden] = useState(true);

  useEffect(() => {
    const dialogLocalState = localStorage.getItem('investigationalUseDialog');
    const dialogSessionState = sessionStorage.getItem('investigationalUseDialog');

    switch (option) {
      case showDialogOption.NeverShowDialog:
        setIsHidden(true);
        break;
      case showDialogOption.AlwaysShowDialog:
        setIsHidden(!!dialogSessionState);
        break;
      case showDialogOption.ShowOnceAndConfigure:
        if (dialogLocalState) {
          const { expiryDate } = JSON.parse(dialogLocalState);
          const isExpired = new Date() > new Date(expiryDate);
          setIsHidden(!isExpired);
        } else {
          setIsHidden(false);
        }
        break;
      default:
        setIsHidden(true);
    }
  }, [option, days]);

  const handleConfirmAndHide = () => {
    const expiryDate = new Date();

    switch (option) {
      case showDialogOption.ShowOnceAndConfigure:
        expiryDate.setDate(expiryDate.getDate() + days);
        localStorage.setItem('investigationalUseDialog', JSON.stringify({ expiryDate }));
        break;
      case showDialogOption.AlwaysShowDialog:
        sessionStorage.setItem('investigationalUseDialog', 'hidden');
        break;
    }
    setIsHidden(true);
  };

  if (isHidden) {
    return null;
  }

  const heightClass = typeof window !== `undefined` && !!navigator.maxTouchPoints ? "h-fit" : "h-[86px]";

  return (
    <div className="fixed bottom-2 z-50 flex w-full justify-center px-2">
      <div className="bg-secondary-dark border-primary-dark flex w-full max-w-4xl items-center justify-between rounded-lg border-2 px-4 py-3 shadow-lg sm:px-6 md:h-[86px] md:px-[22px] md:py-[10px]">
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
          <Icons.LuxmedLogo className="md:h-18 md:w-18 h-12 w-12 flex-shrink-0 sm:h-16 sm:w-16" />
          <div className="flex flex-col">
            <div className="text-sm text-white sm:text-base md:text-[19px]">
              {t('OHIF Viewer is')}{' '}
              <span className="text-primary-light">{t('for investigational use only')}</span>
            </div>
          </div>
        </div>
        <Button
          onClick={handleConfirmAndHide}
          className="bg-primary-main ml-2 flex-shrink-0 text-xs sm:ml-4 sm:text-sm md:text-base"
          data-cy="confirm-and-hide-button"
        >
          {t('Confirm and hide')}
        </Button>
      </div>
    </div>
  );
};

InvestigationalUseDialog.propTypes = {
  dialogConfiguration: PropTypes.shape({
    option: PropTypes.oneOf(Object.values(showDialogOption)).isRequired,
    days: PropTypes.number,
  }),
};

export default InvestigationalUseDialog;
